import { logger } from '../../../utils/logger';

declare global {
  interface Window {
    _fs_app_host?: string;
    _fs_asset_map_id?: string;
    _fs_capture_on_startup?: boolean;
    _fs_cookie_domain?: string;
    _fs_debug?: boolean;
    _fs_dev_mode?: boolean;
    _fs_host?: string;
    _fs_initialized?: boolean;
    _fs_is_outer_script?: boolean;
    _fs_namespace?: string;
    _fs_org?: string;
    _fs_run_in_iframe?: boolean;
    _fs_script?: string;
  }
}

enum FSOperationName {
  TrackEvent = 'trackEvent',
  Log = 'log',
  SetIdentity = 'setIdentity',
  SetProperties = 'setProperties',
  Start = 'start',
  Restart = 'restart',
  Shutdown = 'shutdown',
}

type LogLevel = 'log' | 'info' | 'warn' | 'error';
type PropertyScope = 'document' | 'page' | 'user';

type FSOperation = (typeof FSOperationName)[keyof typeof FSOperationName];

type FSOperationOptions = {
  [FSOperationName.TrackEvent]: {
    name: string;
    properties: object;
    schema?: object;
  };
  [FSOperationName.Log]: {
    level?: LogLevel;
    msg: unknown;
  };
  [FSOperationName.SetIdentity]:
    | {
        anonymous?: boolean;
        consent?: boolean;
        uid?: string | false;
        properties?: object;
        schema?: object;
      }
    | string
    | boolean
    | null
    | undefined;
  [FSOperationName.SetProperties]: {
    type: PropertyScope;
    properties: object;
    schema?: object;
  };
  [FSOperationName.Start]: void;
  [FSOperationName.Restart]: void;
  [FSOperationName.Shutdown]: void;
};

type FSOperationReturnTypes = {
  [FSOperationName.TrackEvent]: void;
  [FSOperationName.Log]: void;
  [FSOperationName.SetIdentity]: void;
  [FSOperationName.SetProperties]: void;
  [FSOperationName.Start]: void;
  [FSOperationName.Shutdown]: void;
  [FSOperationName.Restart]: void;
};

interface FSApi {
  <Op extends FSOperation = FSOperation>(
    operation: Op,
    options?: FSOperationOptions[Op],
    source?: string
  ): FSOperationReturnTypes[Op];
}

const getFullStory = (): FSApi => {
  const fs = window?._fs_namespace ? (window[window._fs_namespace as keyof Window] as FSApi) : undefined;
  if (!fs) throw new Error('FullStory is not initialized');
  return fs;
};

const safelyCall = (fnName: FSOperation) => (options?: FSOperationOptions[FSOperation]) => {
  if (!Object.values(FSOperationName).includes(fnName)) {
    logger.warn(`FullStory function ${fnName} is not available`);
    return;
  }

  const fs = getFullStory();
  logger.log(`Calling FullStory.${fnName} with options:`, options);

  if (window._fs_dev_mode !== true) {
    try {
      fs(fnName, options);
    } catch (e) {
      logger.error(`Error calling FullStory function ${fnName}:`, e);
    }
  }
};

export const FullStory = {
  trackEvent: safelyCall(FSOperationName.TrackEvent),
  log: safelyCall(FSOperationName.Log),
  setIdentity: safelyCall(FSOperationName.SetIdentity),
  setProperties: safelyCall(FSOperationName.SetProperties),
  start: safelyCall(FSOperationName.Start),
  restart: safelyCall(FSOperationName.Restart),
  shutdown: safelyCall(FSOperationName.Shutdown),
};
